import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Eye, Loader2, RefreshCw, X } from 'lucide-react';

import {
  failureLogsAPI,
  type FailureLogDetail,
  type FailureLogEntry,
  type FailureLogKind,
} from './failureLogsApi';
import toast from '../Toast';
import { formatBeijingDateTime } from '../../utils/time';

const PAGE_SIZE = 20;

const kindLabels: Record<FailureLogKind | 'all', string> = {
  all: '全部类型',
  image: '图像',
  video: '视频',
  llm: 'LLM',
};

function formatValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function DetailModal({ detail, onClose }: { detail: FailureLogDetail; onClose: () => void }) {
  const fields = [
    ['任务 ID', detail.jobId],
    ['来源', detail.source],
    ['模型', detail.model],
    ['模式', detail.mode],
    ['阶段', detail.stage],
    ['渠道', detail.channelName],
    ['Provider', detail.provider],
    ['状态', detail.status],
    ['耗时', detail.latencyMs == null ? null : `${detail.latencyMs}ms`],
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-light-border p-4 lg:p-6">
          <div>
            <h3 className="text-lg font-semibold text-light-text">失败日志详情</h3>
            <p className="mt-1 text-sm text-light-muted">{formatBeijingDateTime(detail.createdAt)} · {kindLabels[detail.mediaType]}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 transition-colors hover:bg-light-hover" aria-label="关闭详情">
            <X className="h-5 w-5 text-light-muted" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-auto p-4 lg:p-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map(([label, value]) => (
              <div key={label} className="rounded-xl bg-light-hover p-4">
                <p className="text-xs text-light-muted">{label}</p>
                <p className="mt-1 break-all text-sm text-light-text">{formatValue(value)}</p>
              </div>
            ))}
          </div>
          <DetailBlock title="错误码" value={detail.errorCode} />
          <DetailBlock title="错误信息" value={detail.errorMessage} />
          <DetailBlock title="请求摘要" value={detail.requestSummary} />
          <DetailBlock title="上游响应（已脱敏）" value={detail.rawResponse} />
        </div>
      </div>
    </div>
  );
}

function DetailBlock({ title, value }: { title: string; value: unknown }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <section className="rounded-xl bg-light-hover p-4">
      <h4 className="mb-3 text-sm font-medium text-light-text">{title}</h4>
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words text-xs text-light-text">{formatValue(value)}</pre>
    </section>
  );
}

export default function FailureLogsTab() {
  const [kind, setKind] = useState<FailureLogKind | 'all'>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [logs, setLogs] = useState<FailureLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<FailureLogDetail | null>(null);
  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null);

  const load = async (targetPage = page) => {
    setLoading(true);
    try {
      const result = await failureLogsAPI.list({
        kind: kind === 'all' ? undefined : kind,
        page: targetPage,
        limit: PAGE_SIZE,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      setLogs(result.data);
      setTotal(result.pagination.total);
      setTotalPages(Math.max(result.pagination.totalPages || 1, 1));
      setPage(result.pagination.page || targetPage);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '加载失败日志失败');
      setLogs([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(1);
    // Filters intentionally trigger a fresh first page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, startDate, endDate]);

  const openDetail = async (id: string) => {
    setDetailLoadingId(id);
    try {
      setDetail(await failureLogsAPI.getDetail(id));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '加载失败日志详情失败');
    } finally {
      setDetailLoadingId(null);
    }
  };

  const canPrevious = page > 1 && !loading;
  const canNext = page < totalPages && !loading;
  const description = useMemo(() => `共 ${total} 条，当前第 ${page} / ${totalPages} 页`, [page, total, totalPages]);

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="rounded-2xl border border-light-border bg-white p-4 shadow-sm lg:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="text-sm text-light-muted">
              类型
              <select value={kind} onChange={(event) => setKind(event.target.value as FailureLogKind | 'all')} className="mt-1 block w-full rounded-xl border border-light-border px-3 py-2 text-sm text-light-text focus:border-accent-blue focus:outline-none">
                {Object.entries(kindLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="text-sm text-light-muted">
              开始日期
              <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} className="mt-1 block w-full rounded-xl border border-light-border px-3 py-2 text-sm text-light-text focus:border-accent-blue focus:outline-none" />
            </label>
            <label className="text-sm text-light-muted">
              结束日期
              <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} className="mt-1 block w-full rounded-xl border border-light-border px-3 py-2 text-sm text-light-text focus:border-accent-blue focus:outline-none" />
            </label>
          </div>
          <button onClick={() => void load(page)} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-xl bg-light-hover px-4 py-2 text-sm text-light-text transition-colors hover:bg-light-border disabled:opacity-60">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> 刷新
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-light-border bg-white shadow-sm">
        <div className="border-b border-light-border p-4 lg:p-5">
          <h3 className="text-lg font-semibold text-light-text">统一失败日志</h3>
          <p className="mt-1 text-sm text-light-muted">集中查看图像、视频和 LLM 任务失败记录；详情由服务端完成脱敏后返回。</p>
        </div>
        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full text-sm">
            <thead className="bg-light-hover text-left text-light-muted">
              <tr>
                <th className="px-5 py-3 font-medium">时间</th><th className="px-5 py-3 font-medium">类型</th><th className="px-5 py-3 font-medium">来源 / 模型</th><th className="px-5 py-3 font-medium">渠道</th><th className="px-5 py-3 font-medium">错误摘要</th><th className="px-5 py-3 font-medium">任务 ID</th><th className="px-5 py-3 font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-light-border">
              {loading ? <tr><td colSpan={7} className="px-5 py-10 text-center text-light-muted"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />加载中...</td></tr> : logs.length === 0 ? <tr><td colSpan={7} className="px-5 py-10 text-center text-light-muted">暂无失败日志</td></tr> : logs.map((log) => <LogRow key={log.id} log={log} onView={openDetail} loading={detailLoadingId === log.id} />)}
            </tbody>
          </table>
        </div>
        <div className="divide-y divide-light-border lg:hidden">
          {loading ? <div className="px-5 py-10 text-center text-light-muted"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />加载中...</div> : logs.length === 0 ? <div className="px-5 py-10 text-center text-light-muted">暂无失败日志</div> : logs.map((log) => <LogCard key={log.id} log={log} onView={openDetail} loading={detailLoadingId === log.id} />)}
        </div>
        <div className="flex items-center justify-between border-t border-light-border p-4 text-sm text-light-muted">
          <span>{description}</span>
          <div className="flex items-center gap-2">
            <button disabled={!canPrevious} onClick={() => void load(page - 1)} className="rounded-lg p-2 hover:bg-light-hover disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
            <button disabled={!canNext} onClick={() => void load(page + 1)} className="rounded-lg p-2 hover:bg-light-hover disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
      {detail && <DetailModal detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function LogRow({ log, onView, loading }: { log: FailureLogEntry; onView: (id: string) => void; loading: boolean }) {
  return <tr className="transition-colors hover:bg-light-hover/50"><td className="whitespace-nowrap px-5 py-4 text-xs text-light-muted">{formatBeijingDateTime(log.createdAt)}</td><td className="px-5 py-4"><span className="rounded-lg bg-light-hover px-2 py-1 text-xs text-light-text">{kindLabels[log.mediaType]}</span></td><td className="px-5 py-4 text-light-text">{log.source || '-'}{log.model ? ` / ${log.model}` : ''}</td><td className="px-5 py-4 text-light-text">{log.channelName || '-'}</td><td className="max-w-[360px] px-5 py-4 text-light-text"><div className="truncate" title={log.errorMessage || undefined}>{log.errorCode ? `${log.errorCode}: ` : ''}{log.errorMessage || '-'}</div></td><td className="max-w-[160px] truncate px-5 py-4 font-mono text-xs text-light-text">{log.jobId || '-'}</td><td className="px-5 py-4"><ViewButton onClick={() => onView(log.id)} loading={loading} /></td></tr>;
}

function LogCard({ log, onView, loading }: { log: FailureLogEntry; onView: (id: string) => void; loading: boolean }) {
  return <div className="space-y-3 p-4"><div className="flex items-start justify-between gap-3"><div><p className="font-medium text-light-text">{log.source || log.model || '未知来源'}</p><p className="mt-1 text-xs text-light-muted">{formatBeijingDateTime(log.createdAt)}</p></div><span className="rounded-lg bg-light-hover px-2 py-1 text-xs text-light-text">{kindLabels[log.mediaType]}</span></div><div className="rounded-xl bg-light-hover p-3 text-xs text-light-text break-words">{log.errorCode ? `${log.errorCode}: ` : ''}{log.errorMessage || '-'}</div><p className="break-all font-mono text-xs text-light-muted">任务 ID：{log.jobId || '-'}</p><ViewButton onClick={() => onView(log.id)} loading={loading} fullWidth /></div>;
}

function ViewButton({ onClick, loading, fullWidth = false }: { onClick: () => void; loading: boolean; fullWidth?: boolean }) {
  return <button onClick={onClick} disabled={loading} className={`inline-flex items-center justify-center gap-1.5 rounded-lg bg-accent-blue px-3 py-1.5 text-xs text-white transition-colors hover:bg-accent-blue/90 disabled:opacity-60 ${fullWidth ? 'w-full py-2 text-sm' : ''}`}>{loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}{loading ? '加载中' : '查看详情'}</button>;
}
